import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Col, Card, CardBody, Container, Input, Label, Row, Spinner, FormGroup } from 'reactstrap';
import { Btn, H3 } from '../../../AbstractElements';
import { get, postJson, putJson } from '../../../hook/request';
import { toast } from 'react-toastify';

// --- Tipi risposta EDIT ---
type EditItem = {
  ID: number;
  nome: string;
  ordine: number;
  campo: string;
  groupName: string;
  rdvID: number;
};
type EditResponse = {
  message: {
    status: string;
    list: EditItem[];
  };
};

// --- Tipi nuove select ---
type RdvItem = { ID: number; nome: string; data_ins: string };
type FieldItem = { ID: number; type: string; campo: string; nome: string; ordine: number };

type SaveGroupResponse = {
  message?: string;
  id?: string | number;
  field: string;
  status?: 'ok' | 'error' | string;
};

export default function FormCluster() {
  const { slug } = useParams<{ slug: string }>();
  const isEdit = useMemo(() => !!slug && slug !== 'new', [slug]);

  const [loading, setLoading] = useState<boolean>(isEdit);
  const [saving, setSaving] = useState(false);

  // Select: File caricati (RDV)
  const [rdvList, setRdvList] = useState<RdvItem[]>([]);

  const [selectedRdv, setSelectedRdv] = useState<number | ''>('');

  // Select: Campo (da /typeOrder/Cluster)
  const [fieldOptions, setFieldOptions] = useState<FieldItem[]>([]);
  const [selectedField, setSelectedField] = useState<string>(''); // campo

  const [fieldLoading, setFieldLoading] = useState<boolean>(false);

  // liste disponibili/gruppo
  const [available, setAvailable] = useState<string[]>([]);
  const [group, setGroup] = useState<string[]>([]);

  // selezioni (come set di indici)
  const [selLeftSet, setSelLeftSet] = useState<Set<number>>(new Set());
  const [selRightSet, setSelRightSet] = useState<Set<number>>(new Set());

  const [groupName, setGroupName] = useState<string>('');
  const [fieldOptionsLoading, setFieldOptionsLoading] = useState<boolean>(false);


  const busy = loading;

  // ================== LOAD opzioni: File Caricati + Campo ==================
  useEffect(() => {
    (async () => {
      try {
        const r1 = await get<any>('/rdv?limit=5');
        const rdvPayload = (r1 as any)?.data ?? r1;
        const rdv: RdvItem[] = Array.isArray(rdvPayload?.list) ? rdvPayload.list : [];
        setRdvList(rdv);
        if (!isEdit && rdv.length > 0) {
          setSelectedRdv(rdv[0].ID);
        }

        setFieldOptionsLoading(true);
        const r2 = await get<any>('/typeOrder/Cluster');
        const fPayload = (r2 as any)?.data ?? r2;
        const fieldsRaw: FieldItem[] = Array.isArray(fPayload?.list) ? fPayload.list : [];
        const fields: FieldItem[] = fieldsRaw.sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0));
        setFieldOptions(fields);

        
        // if (!isEdit) {
        //   setSelectedRdv('');     
        //   setSelectedField('');  
        // }
      } catch {
        toast.error('Errore nel caricamento delle opzioni');
      } finally {
        setFieldOptionsLoading(false);
      }
    })();
  }, [isEdit]);


  // ================== INIT IN EDIT ==================
  useEffect(() => {
    if (!isEdit) return;
    if (!slug) return;

    // ⛔ se non ho ancora caricato gli RDV, non faccio niente
    if (rdvList.length === 0) return;

    let mounted = true;

    const loadEdit = async () => {
      try {
        setLoading(true);

        // 1) Dettaglio gruppo
        const res = await get<EditResponse>(
          `/cluster/edit?groupName=${encodeURIComponent(String(slug))}`
        );
        const list =
          res?.data?.message?.list ??
          (res as any)?.message?.list ??
          [];

        if (!Array.isArray(list) || list.length === 0) {
          toast.warn('Nessun dato per questo gruppo.');
          return;
        }

        const field = String(list[0].campo);
        const gName = String(list[0].groupName);

        // 2) Decido quale RDV usare
        const rdvFromEdit = list[0].rdvID; // numero
        const exists = rdvList.some(r => r.ID === rdvFromEdit);
        const rdvToUse = exists ? rdvFromEdit : rdvList[0].ID;

        if (!mounted) return;

        setGroupName(gName);
        setSelectedField(field);
        setSelectedRdv(rdvToUse);
        await loadFieldValues(field, rdvToUse);
        if (!mounted) return;
        const ordered = [...list]
          .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
          .map(x => x.nome);

        setGroup(ordered);
        setAvailable(prev => prev.filter(v => !ordered.includes(v)));

        setSelLeftSet(new Set());
        setSelRightSet(new Set());
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          'Errore nel caricamento';
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    loadEdit();
    return () => {
      mounted = false;
    };
  }, [isEdit, slug, rdvList]); 


  // ================== LOAD valori per campo scelto (con rdv) ==================
  const loadFieldValues = async (field: string, rdv?: number): Promise<void> => {
    setSelectedField(field);
    try {
      setFieldLoading(true);
      const params = new URLSearchParams();
      params.set('group', field);
      if (rdv) params.set('rdv', String(rdv));
      const res = await get<any>(`/cluster/campo?${params.toString()}`);
      const raw = (res as any)?.data ?? res;

      const list: any[] =
        Array.isArray(raw?.message?.list) ? raw.message.list :
          Array.isArray(raw?.list) ? raw.list :
            Array.isArray(raw) ? raw : [];

      const values = Array.from(
        new Set(
          list
            .map((row: any) => row?.[field] ?? row?.data ?? row)
            .map((v: any) => (v == null ? '' : String(v)).trim())
            .filter(Boolean)
        )
      );

      setAvailable(values);
      if (!isEdit) setGroup([]);
      setSelLeftSet(new Set());
      setSelRightSet(new Set());
    } finally {
      setFieldLoading(false);
    }
  };

  // cambio campo
  const handleRdvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.currentTarget.value;
    const val = v ? Number(v) : '';
    setSelectedRdv(val);
    if (selectedField) {
      await loadFieldValues(selectedField, typeof val === 'number' ? val : undefined);
      setGroup([]);
    }
  };

  const handleFieldChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextField = e.currentTarget.value;
    setSelectedField(nextField);
    await loadFieldValues(nextField, typeof selectedRdv === 'number' ? selectedRdv : undefined);
    setGroup([]);
  };


  // ================== AZIONI LISTE ==================
  const toggleLeft = (idx: number) => {
    setSelLeftSet((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const toggleRight = (idx: number) => {
    setSelRightSet((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };
  const addToGroup = () => {
    const selLeft = Array.from(selLeftSet);
    if (selLeft.length === 0) return;
    const selectedLabels = selLeft.map((i) => available[i]);
    const toAdd = selectedLabels.filter((lbl) => !group.includes(lbl));
    setGroup((g) => [...g, ...toAdd]);
    setAvailable((av) => av.filter((_, idx) => !selLeft.includes(idx)));
    setSelLeftSet(new Set());
  };
  const removeFromGroup = () => {
    const selRight = Array.from(selRightSet);
    if (selRight.length === 0) return;
    const removed = selRight.map((i) => group[i]);
    const keep = group.filter((_, idx) => !selRight.includes(idx));
    setGroup(keep);
    setAvailable((av) => Array.from(new Set([...av, ...removed])));
    setSelRightSet(new Set());
  };
  const moveUp = () => {
    const indices = Array.from(selRightSet).sort((a, b) => a - b);
    if (indices.length === 0) return;
    const newGroup = [...group];
    for (const idx of indices) {
      if (idx === 0) continue;
      if (!indices.includes(idx - 1)) {
        [newGroup[idx - 1], newGroup[idx]] = [newGroup[idx], newGroup[idx - 1]];
      }
    }
    const newSel = new Set(indices.map((i) => (i > 0 && !indices.includes(i - 1) ? i - 1 : i)));
    setGroup(newGroup);
    setSelRightSet(newSel);
  };
  const moveDown = () => {
    const indices = Array.from(selRightSet).sort((a, b) => b - a);
    if (indices.length === 0) return;
    const newGroup = [...group];
    for (const idx of indices) {
      if (idx === newGroup.length - 1) continue;
      if (!indices.includes(idx + 1)) {
        [newGroup[idx + 1], newGroup[idx]] = [newGroup[idx], newGroup[idx + 1]];
      }
    }
    const newSel = new Set(
      indices.map((i) => (i < newGroup.length - 1 && !indices.includes(i + 1) ? i + 1 : i)).sort((a, b) => a - b)
    );
    setGroup(newGroup);
    setSelRightSet(newSel);
  };

  // ================== SAVE (POST/PUT) ==================
  const saveGroup = async () => {
    if (!groupName.trim()) {
      alert('Inserisci un nome per il gruppo.');
      return;
    }
    if (group.length === 0) {
      alert('Aggiungi almeno un elemento al gruppo.');
      return;
    }
    if (!selectedField) {
      alert('Seleziona un campo.');
      return;
    }

    const user_email = localStorage.getItem('email');
    const payload = {
      groupName,
      items: group,
      user_email,
      field: selectedField,
      // se serve inviare anche l'rdv al salvataggio, aggiungi: rdv: selectedRdv || null
    };

    try {
      setSaving(true);
      if (isEdit) {
        await putJson<SaveGroupResponse>('/cluster', payload);
        toast.success('Gruppo aggiornato!');
      } else {
        await postJson<SaveGroupResponse>('/cluster', payload);
        toast.success('Gruppo salvato con successo!');
        await loadFieldValues(selectedField, typeof selectedRdv === 'number' ? selectedRdv : undefined);
        setGroupName('');
        setGroup([]);
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg ?? `Errore nel salvataggio del gruppo${status ? ` (HTTP ${status})` : ''}.`);
    } finally {
      setSaving(false);
    }
  };

  // ================== UI ==================
  return (
    <div className="page-body pt-4">
      <Container fluid className="pt-2">
        <Card className="p-3">

          <Row>
            <div className="mb-3 d-flex justify-content-between">
              <H3>{isEdit ? 'Modifica Gruppo' : 'Nuovo Gruppo'}</H3>
              <Link to="/configurazione/cluster">
                <Btn className="px-4" color="primary" type="button">
                  Torna Indietro
                </Btn>
              </Link>
            </div>

            <div>
              <Col md='12'>
                <Label className="fw-semibold">File caricati</Label>
                <FormGroup>
                  <Input
                    type="select"
                    value={selectedRdv}
                    onChange={handleRdvChange}
                    style={{ width: 320, height: 40 }}
                  >
                    <option value="">Seleziona file…</option>
                    {rdvList.map(r => (
                      <option key={r.ID} value={r.ID}>{r.nome}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </div>


            <div className="d-flex flex-wrap mb-3 gap-3">
              <div>
                <Col>
                  <Label className="fw-semibold">Gruppo</Label>
                  <FormGroup>
                    <Input
                      type="text"
                      placeholder="Nome gruppo"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      style={{ width: 280, height: 40 }}
                      readOnly={isEdit}
                    />
                  </FormGroup>
                </Col>
              </div>


              {/* Campo */}
              <div>
                <Col>
                  <Label className="fw-semibold">Campo</Label>
                  <FormGroup>
                    <Input
                      type="select"
                      value={selectedField}
                      onChange={handleFieldChange}
                      style={{ width: 320, height: 40 }}
                      disabled={isEdit || fieldOptionsLoading}
                    >
                      {fieldOptionsLoading ? (
                        <option value="">Caricamento…</option>
                      ) : (
                        <option value="">Scegli campo…</option>
                      )}
                      {fieldOptions.map(f => (
                        <option key={f.ID} value={f.campo}>{f.nome}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </div>
            </div>
          </Row>

          <Row className="g-4 align-items-start">
            {/* Sinistra: Disponibili */}
            <Col xl={5}>
              <Label className="fw-semibold mb-2">Disponibili</Label>
              {fieldLoading ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" /> Caricamento…
                </div>
              ) : (
                <div role="listbox" aria-multiselectable="true" className="border rounded p-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {available.length === 0 ? (
                    <div className="text-muted">Nessun elemento</div>
                  ) : (
                    available.map((label, idx) => (
                      <Fragment key={`${label}-${idx}`}>
                        <Label className="d-block">
                          <Input
                            className="checkbox_animated"
                            checked={selLeftSet.has(idx)}
                            type="checkbox"
                            onChange={() => toggleLeft(idx)}
                          />
                          {label}
                        </Label>
                      </Fragment>
                    ))
                  )}
                </div>
              )}
            </Col>

            {/* Centro: azioni */}
            <Col xl={2} className="d-flex flex-column align-items-center gap-2 pt-4">
              <Btn style={{ width: 150 }} color="primary" outline onClick={addToGroup} disabled={fieldLoading || available.length === 0}>
                <i className="iconly-Arrow-Right icli" /> Aggiungi
              </Btn>
              <Btn style={{ width: 150 }} color="secondary" outline onClick={removeFromGroup} disabled={group.length === 0 || selRightSet.size === 0}>
                <i className="iconly-Arrow-Left icli" /> Rimuovi
              </Btn>
              <hr className="w-100" />
              <Btn style={{ width: 150 }} color="secondary" onClick={moveUp} disabled={group.length === 0 || selRightSet.size === 0}>
                <i className="iconly-Arrow-Up icli" /> Su
              </Btn>
              <Btn style={{ width: 150 }} color="secondary" onClick={moveDown} disabled={group.length === 0 || selRightSet.size === 0}>
                <i className="iconly-Arrow-Down icli" /> Giù
              </Btn>
            </Col>

            {/* Destra: Gruppo */}
            <Col xl={5}>
              <Label className="fw-semibold mb-2">Gruppo</Label>
              <div role="listbox" aria-multiselectable="true" className="border rounded p-2" style={{ maxHeight: 420, overflowY: 'auto' }}>
                {group.length === 0 ? (
                  <div className="text-muted">Nessun elemento</div>
                ) : (
                  group.map((label, idx) => (
                    <Fragment key={`${label}-${idx}-r`}>
                      <Label className="d-block">
                        <Input
                          type="checkbox"
                          className="checkbox_animated"
                          checked={selRightSet.has(idx)}
                          onChange={() => toggleRight(idx)}
                        />
                        {label}
                      </Label>
                    </Fragment>
                  ))
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <div className="mt-3 d-flex justify-content-end">
                <Button style={{ width: 200 }} color="success" onClick={saveGroup} disabled={saving || fieldLoading}>
                  {saving ? 'Salvataggio…' : isEdit ? 'Aggiorna gruppo' : 'Salva gruppo'}
                </Button>
              </div>
            </Col>
          </Row>
        </Card>
      </Container>
    </div>
  );
}
